<script setup lang="ts">
//#region imports
import { cloneDeep } from 'lodash-es';
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useActivitiesLanguage } from '@/views/CRM/composables/useActivitiesLanguage';
import { useSmsStat } from '@/views/CRM/composables/useSmsStat';
import LanguagesSetup from '@/components/UIElements/LanguagesSetup.vue';
import { useTypedStore } from '@/store';
import type { EmitsToListeners } from '@/types/core';
import type { CrmAction, CrmActionGroup, CrmActivity } from '@/types/crm';
//#endregion

//#region types
interface Props {
  activity?: CrmActivity;
  group: CrmActionGroup;
  payload?: { actions?: CrmAction[]; index?: number } | null;
}

interface Emits {
  'send-test-sms': [{ actionGroup: CrmActionGroup; index: number }];
  close: [];
}

export interface AddSmsActionPreviewParams {
  data?: Props;
  listeners?: EmitsToListeners<Emits>;
}
//#endregion

//#region define
const props = withDefaults(defineProps<Props>(), {
  activity: () => ({ action_groups: [] } as unknown as CrmActivity),
});
const emit = defineEmits<Emits>();
//#endregion

//#region const
const store = useTypedStore();
const { t } = useI18n();
//#endregion

//#region composables
const isVisible = ref(false);
const index = ref(0);
const actionGroup = ref<CrmActionGroup | null>(null);
const actionGroupIndex = ref(-1);
const actions = ref<CrmAction[]>([]);
const name = ref('');
const message = ref('');

const languagesFromStore = computed(() => store.state.misc?.languages || []);

const {
  languages,
  handleSelectTab,
} = useActivitiesLanguage<CrmAction>({
  actions,
  index,
  actionGroupIndex,
  languagesFromStore,
});

const { messageStats, messageStyle } = useSmsStat({ message });

const popupTitle = computed(() => {
  const title = String(t('crm.activities.actions.action_popup.sms_title'));
  return name.value ? `${title} (${name.value})` : title;
});
//#endregion

//#region methods
function getDefaultForm(): CrmAction {
  return {
    name: '',
    type: 'sms',
    message: '',
    language_code: 'en',
    is_default: true,
  } as unknown as CrmAction;
}

function handleSendTestSms() {
  emit('send-test-sms', {
    actionGroup: {
      ...(actionGroup.value || {}),
      actions: actions.value,
    } as CrmActionGroup,
    index: index.value,
  });
}

function open() {
  const group = props.group;
  const payload = props.payload;

  actionGroup.value = group;
  actionGroupIndex.value = props.activity.action_groups.indexOf(group);

  const sourceActions = (props.activity.action_groups[actionGroupIndex.value]?.actions || [])
    .filter((item) => item.type === 'sms');

  if (!sourceActions.length) {
    sourceActions.push(getDefaultForm());
  }

  actions.value = cloneDeep(payload?.actions || sourceActions);
  index.value = typeof payload?.index === 'number' ? payload?.index : 0;

  const action = actions.value[index.value];
  if (action) {
    message.value = action.message;
    name.value = action.name;
  }

  isVisible.value = true;
}

function close() {
  isVisible.value = false;
  emit('close');
}
//#endregion

//#region watch
watch(index, (nextIndex) => {
  const action = actions.value[nextIndex];
  if (action) {
    message.value = action.message;
    name.value = action.name;
  }
}, { immediate: true });
//#endregion

//#region hooks
defineExpose({
  open,
  close,
});
//#endregion
</script>

<template>
  <Popup
    v-model:is-visible="isVisible"
    :title="popupTitle"
    :width="800"
    class="add-sms-action-preview-popup"
    @close="close()"
  >
    <template v-slot:body>
      <div class="t:-my-16 ">
        <LanguagesSetup
          class="t:-mx-24"
          :can-edit="false"
          :selected-index="index"
          :languages="languages"
          @select-index="handleSelectTab"
        >
          <div class="t:space-y-8">
            <UiTextarea
              v-model="message"
              readonly
              :label="$t('crm.activities.actions.action_popup.sms_fields.message')"
              :placeholder="$t('crm.activities.actions.action_popup.sms_fields.message')"
              :rows="6"
            />
            <UiHelp
              :text="messageStats"
              :style="messageStyle"
            />
          </div>
        </LanguagesSetup>
      </div>
    </template>
    <template v-slot:footer>
      <div class="t:flex t:justify-end t:space-x-8">
        <UiButton
          icon="paper-plane"
          @click="handleSendTestSms()"
        >
          {{ $t('crm.buttons.send_test_sms') }}
        </UiButton>
        <UiButton
          type="negative"
          icon="times"
          @click="close()"
        >
          {{ $t('crm.buttons.close') }}
        </UiButton>
      </div>
    </template>
  </Popup>
</template>

<style lang="scss">
.add-sms-action-preview-popup {
  .popup__header{
    border: none;
  }
}
</style>
